import { useState, useRef, useCallback, useEffect } from 'react'

interface UseVoiceRecorderOptions {
  onStreamReady?: (stream: MediaStream) => void
  onStreamEnd?: () => void
}

interface UseVoiceRecorderResult {
  isRecording: boolean
  isPending: boolean // True while waiting for mic permission
  audioBlob: Blob | null
  duration: number // Recording duration in seconds
  startRecording: () => void
  stopRecording: () => void
  cancelRecording: () => void
  error: string | null
}

export function useVoiceRecorder(options: UseVoiceRecorderOptions = {}): UseVoiceRecorderResult {
  const { onStreamReady, onStreamEnd } = options
  const [isRecording, setIsRecording] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // Track recording duration
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>
    if (isRecording) {
      setDuration(0)
      interval = setInterval(() => {
        setDuration((d) => d + 1)
      }, 1000)
    }
    return () => clearInterval(interval)
  }, [isRecording])

  const mediaRecorder = useRef<MediaRecorder | null>(null)
  const audioChunks = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)
  const shouldStopRef = useRef(false) // Track if stop was called during init
  const shouldCancelRef = useRef(false) // Track if cancel was called during init

  const startRecording = useCallback(() => {
    setError(null)
    setAudioBlob(null)
    audioChunks.current = []
    shouldStopRef.current = false
    shouldCancelRef.current = false
    setIsPending(true)

    // Start async initialization
    navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      })
      .then((stream) => {
        // Check if stop/cancel was called while waiting for permission
        if (shouldCancelRef.current) {
          stream.getTracks().forEach((track) => track.stop())
          setIsPending(false)
          return
        }

        streamRef.current = stream

        // Notify caller that stream is ready (for audio analyser)
        onStreamReady?.(stream)

        // Prefer webm/opus for better compression
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm'

        const recorder = new MediaRecorder(stream, { mimeType })
        mediaRecorder.current = recorder

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunks.current.push(event.data)
          }
        }

        recorder.onstop = () => {
          // Notify caller that stream ended (for audio analyser cleanup)
          onStreamEnd?.()

          // Only create blob if not cancelled
          if (!shouldCancelRef.current && audioChunks.current.length > 0) {
            const blob = new Blob(audioChunks.current, { type: mimeType })
            setAudioBlob(blob)
          }

          // Clean up stream
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop())
            streamRef.current = null
          }
        }

        recorder.start(100) // Collect data every 100ms
        setIsPending(false)
        setIsRecording(true)

        // If stop was called during init, stop now
        if (shouldStopRef.current) {
          recorder.stop()
          setIsRecording(false)
        }
      })
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to access microphone'
        setError(message)
        setIsPending(false)
        console.error('Recording error:', err)
      })
  }, [])

  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop()
      setIsRecording(false)
    } else {
      // Recording not started yet - mark for stop when ready
      shouldStopRef.current = true
    }
  }, [])

  const cancelRecording = useCallback(() => {
    shouldCancelRef.current = true

    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop()
    } else {
      // If recorder didn't start, still notify stream end
      onStreamEnd?.()
    }

    // Clean up stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }

    audioChunks.current = []
    setAudioBlob(null)
    setIsRecording(false)
    setIsPending(false)
  }, [onStreamEnd])

  return {
    isRecording,
    isPending,
    audioBlob,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
  }
}

// Utility to convert Blob to base64
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      // Remove data URL prefix
      const base64Data = base64.split(',')[1]
      resolve(base64Data)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}
